import { NavigateFunction } from 'react-router-dom'

import {
  UploaderParams,
  SocketInstanceResources,
  OpenAppParams,
  PendingAsyncOperation,
  AsyncOperationProgress,
  AsyncOperationType,
  SocketOpenAppTaskResultMessage
} from './constants'
import {
  SocketActionTypes,
  SocketProgressState,
  WebSocketConnectionStatus,
  AsyncOperationResult,
  PersistentOperation,
  ScheduledTaskUpdate
} from './types'
import { StatusInternalTests } from 'common/types/internalTests'
import { AgentPresence } from './agentLiveView'

/**
 * start channel
 */
export const startWSServer = (navigate: NavigateFunction) => ({
  type: SocketActionTypes.START_SERVER,
  payload: { navigate }
})

/**
 * stop channel
 */
export const stopWSServer = () => ({ type: SocketActionTypes.STOP_SERVER })

/**
 * set global app progress
 */
export const setProgress = (payload: SocketProgressState) => ({
  type: SocketActionTypes.SET_PROGRESS,
  payload
})
export const setProgressBar = (payload: SocketProgressState) => ({
  type: SocketActionTypes.SET_PROGRESSBAR,
  payload
})
export const hideProgressBar = () => ({
  type: SocketActionTypes.HIDE_PROGRESSBAR
})

/**
 * Update resources for the current instance
 */
export const updateInstanceResources = (payload: SocketInstanceResources) => ({
  type: SocketActionTypes.UPDATE_INSTANCE_RESOURCES,
  payload
})

export const setUploaderParams = (payload?: UploaderParams) => ({
  type: SocketActionTypes.SET_UPLOADER_PARAMS,
  payload
})

export const internalTestsStatus = (payload: StatusInternalTests) => ({
  type: SocketActionTypes.INTERNAL_TESTS_STATUS,
  payload
})

export const resetInternalTests = () => ({
  type: SocketActionTypes.RESET_INTERNAL_TESTS
})

/**
 * App Lock Info
 */
export const setAppLockInfo = (appIsLocked: boolean) => ({
  type: SocketActionTypes.SET_APP_LOCK_INFO,
  payload: appIsLocked
})

/**
 * Refresh app notifications
 */
export const setRefreshAppNotifications = (refresh: boolean) => ({
  type: SocketActionTypes.SET_REFRESH_APP_NOTIFICATIONS,
  payload: refresh
})

/**
 * A scheduled task changed. `seq` is added by the reducer.
 */
export const setScheduledTaskUpdate = (update: Omit<ScheduledTaskUpdate, 'seq'>) => ({
  type: SocketActionTypes.SET_SCHEDULED_TASK_UPDATE,
  payload: update
})

/**
 * Channel connection status
 */
export const setPreviousWebSocketConnectionStatus = (status: WebSocketConnectionStatus) => ({
  type: SocketActionTypes.SET_WEBSOCKET_PREVIOUS_CONNECTION_STATUS,
  payload: status
})
/**
 * Translations progress
 */
export const setTranslationsProgress = (progress: number) => ({
  type: SocketActionTypes.TRANSLATIONS_PROGRESS,
  payload: progress
})
export const resetTranslationsProgress = () => ({
  type: SocketActionTypes.RESET_TRANSLATIONS_PROGRESS
})

export const setOpenAppWithUnconfirmedChanges = (data?: OpenAppParams) => ({
  type: SocketActionTypes.OPEN_APP_WITH_UNCONFIRMED_CHANGES,
  payload: data
})

export const addPendingAsyncOperation = (operation: PendingAsyncOperation) => ({
  type: SocketActionTypes.ADD_PENDING_FILE_OPERATION,
  payload: operation
})

export const removePendingAsyncOperation = (operationId: string) => ({
  type: SocketActionTypes.REMOVE_PENDING_FILE_OPERATION,
  payload: operationId
})

export const setAsyncOperationResult = (result: AsyncOperationResult) => ({
  type: SocketActionTypes.SET_FILE_OPERATION_RESULT,
  payload: result
})

export const clearAsyncOperationResult = () => ({
  type: SocketActionTypes.CLEAR_FILE_OPERATION_RESULT
})

export const setAsyncOperationProgress = (progress: AsyncOperationProgress) => ({
  type: SocketActionTypes.SET_ASYNC_OPERATION_PROGRESS,
  payload: progress
})

export const clearAsyncOperationProgress = () => ({
  type: SocketActionTypes.CLEAR_ASYNC_OPERATION_PROGRESS
})

export const setPersistentOperation = (operation: PersistentOperation) => ({
  type: SocketActionTypes.SET_PERSISTENT_OPERATION,
  payload: operation
})

export const clearPersistentOperation = () => ({
  type: SocketActionTypes.CLEAR_PERSISTENT_OPERATION
})

export const startOperationStatusPolling = (
  operationId: string,
  operationType: AsyncOperationType
) => ({
  type: SocketActionTypes.START_OPERATION_STATUS_POLLING,
  payload: { operationId, operationType }
})

export const stopOperationStatusPolling = () => ({
  type: SocketActionTypes.STOP_OPERATION_STATUS_POLLING
})

export const setForceStopPhase = (phase: string | null) => ({
  type: SocketActionTypes.SET_FORCE_STOP_PHASE,
  payload: phase
})

export const setAgentPresence = (presence?: AgentPresence) => ({
  type: SocketActionTypes.SET_AGENT_PRESENCE,
  payload: presence
})

export const setFollowAgent = (follow: boolean) => ({
  type: SocketActionTypes.SET_FOLLOW_AGENT,
  payload: follow
})

export const setOpenAppTaskResult = (result: SocketOpenAppTaskResultMessage) => ({
  type: SocketActionTypes.OPEN_APP_TASK_RESULT,
  payload: result
})
